import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { appChannelConfigApi } from '../../api/channelConfig.api';
import type { ChannelConfig, ChannelConfigInput } from '../../schemas/channelConfig.schema';
import { CHANNELS } from '../../schemas/channelConfig.schema';
import { isManagedByCac, ManagedByCacBadge } from '../shared/ManagedByCac';

interface EditState {
  channel: string;
  fromAddress: string;
  fromName: string;
  replyTo: string;
}

interface AppChannelConfigSectionProps {
  /**
   * The app whose channel config this section edits.
   *
   * Required because the backend resolves the app from `?appId` for a
   * SUPERUSER: mounted without it, the list came back empty (or 400) and any
   * write landed with no app context (noten-p9t). The backoffice is a
   * SUPERUSER surface, so this is the normal path here, not an edge case.
   */
  appId: string | undefined;
}

export function AppChannelConfigSection({ appId }: AppChannelConfigSectionProps) {
  const scoped = appChannelConfigApi.withQuery(appId ? { appId } : {});
  const { data, loading, error, refetch } = scoped.useList();
  const { mutate: updateConfig } = scoped.useUpdate();
  const { mutate: deleteConfig } = scoped.useDelete();
  const { showSnackbar } = useSnackbar();
  const [editState, setEditState] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  const configByChannel = new Map<string, ChannelConfig>();
  for (const c of data ?? []) {
    configByChannel.set(c.channel, c);
  }

  const handleEdit = (channel: string) => {
    const existing = configByChannel.get(channel);
    setEditState({
      channel,
      fromAddress: existing?.fromAddress ?? '',
      fromName: existing?.fromName ?? '',
      replyTo: existing?.replyTo ?? '',
    });
  };

  const handleSave = async () => {
    if (!editState) return;
    setSaving(true);
    try {
      const input: ChannelConfigInput = {
        fromAddress: editState.fromAddress,
        fromName: editState.fromName || undefined,
        replyTo: editState.replyTo || undefined,
      };
      await updateConfig(editState.channel, input);
      await refetch();
      showSnackbar({ message: `${editState.channel} config saved`, variant: 'success' });
      setEditState(null);
    } catch {
      showSnackbar({ message: 'Failed to save channel config', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (channel: string) => {
    try {
      await deleteConfig(channel);
      await refetch();
      showSnackbar({ message: `${channel} config removed`, variant: 'success' });
    } catch {
      showSnackbar({ message: 'Failed to remove channel config', variant: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Channel config</CardTitle>
        <CardDescription>Per-channel "from" address defaults for this app.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-destructive">
            Failed to load channel configs.{' '}
            <button
              type="button"
              onClick={() => refetch()}
              className="font-medium text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead>From address</TableHead>
                <TableHead>From name</TableHead>
                <TableHead>Reply to</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {CHANNELS.map((ch) => {
                const config = configByChannel.get(ch);
                const isEditing = editState?.channel === ch;

                if (isEditing && editState) {
                  return (
                    <TableRow key={ch} className="bg-muted/30">
                      <TableCell>
                        <ChannelBadge channel={ch as ChannelKind} />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editState.fromAddress}
                          onChange={(e) =>
                            setEditState({ ...editState, fromAddress: e.target.value })
                          }
                          placeholder="noreply@app.com"
                          className="h-8 text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editState.fromName}
                          onChange={(e) => setEditState({ ...editState, fromName: e.target.value })}
                          placeholder="My App"
                          className="h-8 text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editState.replyTo}
                          onChange={(e) => setEditState({ ...editState, replyTo: e.target.value })}
                          placeholder="support@app.com"
                          className="h-8 text-sm"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={!editState.fromAddress || saving}
                          >
                            {saving ? '…' : 'Save'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditState(null)}>
                            Cancel
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <TableRow key={ch}>
                    <TableCell>
                      <ChannelBadge channel={ch as ChannelKind} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {config?.fromAddress ?? (
                        <span className="text-muted-foreground">Not configured</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {config?.fromName ?? <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {config?.replyTo ?? <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {isManagedByCac(config) ? (
                        <ManagedByCacBadge />
                      ) : (
                        <div className="inline-flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(ch)}>
                            <Pencil /> {config ? 'Edit' : 'Configure'}
                          </Button>
                          {config ? (
                            <ConfirmAction
                              trigger={
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 /> Remove
                                </Button>
                              }
                              title={`Remove ${ch} channel config?`}
                              description="The app will fall back to defaults. You can reconfigure anytime."
                              confirmLabel="Remove"
                              destructive
                              onConfirm={() => handleDelete(ch)}
                            />
                          ) : null}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
